import type { ReactNode } from 'react';

import { Box, CheveronSmallLeftOutline, CheveronSmallRightOutline, HStack, IconButton, Kbd, Tooltip } from '@bonusly/design-system';

// The wide controls stay on a fixed centerline while cards of different
// heights page underneath them.
const WIDE_DECK_NAV_TOP = 32;

export function ShortcutKey({ children }: { children?: ReactNode }) {
	return (
		<Kbd bg="whiteAlpha.300" border="none" borderRadius="sm" color="inherit" fontFamily="inherit" fontSize="xs" fontWeight="medium" px={1.5} py={0.5}>
			{children}
		</Kbd>
	);
}

export function DeckNavigationButton({
	direction,
	label,
	placement,
	isDisabled,
	onClick,
}: {
	direction: 'prev' | 'next';
	label: string;
	placement: 'edge' | 'inline';
	isDisabled: boolean;
	onClick: () => void;
}) {
	const isPrevious = direction === 'prev';
	const isInline = placement === 'inline';
	const edgePosition = '-16px';
	const shortcut = isPrevious ? 'ArrowLeft' : 'ArrowRight';
	const shortcutGlyph = isPrevious ? '←' : '→';

	return (
		<Tooltip
			label={
				<HStack as="span" spacing={1.5}>
					<Box as="span">{label}</Box>
					<ShortcutKey>{shortcutGlyph}</ShortcutKey>
				</HStack>
			}
			hasArrow={false}
			isDisabled={isDisabled}
		>
			<IconButton
				aria-label={label}
				aria-keyshortcuts={isDisabled ? undefined : shortcut}
				icon={isPrevious ? <CheveronSmallLeftOutline boxSize="24px" /> : <CheveronSmallRightOutline boxSize="24px" />}
				isRound
				variant="ghost"
				position={isInline ? 'relative' : 'absolute'}
				top={isInline ? undefined : WIDE_DECK_NAV_TOP}
				left={!isInline && isPrevious ? edgePosition : undefined}
				right={!isInline && !isPrevious ? edgePosition : undefined}
				transform={isInline ? undefined : 'translateY(-50%)'}
				zIndex={3}
				boxSize="40px"
				minW="40px"
				p={2}
				bg="whiteAlpha.900"
				color="text.default"
				borderWidth="1px"
				borderColor="border.soft"
				boxShadow={isInline ? 'none' : 'base'}
				_hover={{ bg: 'bg.surface', borderColor: 'border.default' }}
				_active={{ bg: 'bg.surface' }}
				isDisabled={isDisabled}
				onClick={onClick}
				data-deck-nav={direction}
			/>
		</Tooltip>
	);
}
